import { Component, Injector, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription, switchMap, takeUntil } from "rxjs";
import { map } from "rxjs/operators";
import { KeyViewModel } from "../Model/KeyViewModel";
import { CreateKeyComponent } from "../create-key.component";
import { EditKeyComponent } from "../edit-key.coponent";
import { KeysService } from "../../../../services/keys.service";
import { KeyObjectsSyncDialogComponent } from "../keyobjects-sync-dialog/keyobjects-sync-dialog.component";
import { ExcelService } from "../../../../services/ExcelService";
import { GroupBaseAdminPanelComponent } from '../../base-admin-panel/group-base-admin-panel.component';
import { IManagableObject } from '../../IManagableObject';
import { DriversService } from "../../../../services/drivers.service";
import { ContainerAcl } from "app/models/ContainerAcl";
import { ContainersService } from "app/services/containers.service";
import { ContainerViewModel } from "../../ContainerViewModel";
import { GroupManagementDialogComponent } from "../../group-management-dialog/group-management-dialog.component";
import { ObjectsManagementDialogComponent } from "../../objects-management-dialog/objects-management-dialog.component";
import { ObjectGroupViewModel } from "../../ObjectGroupViewModel";
import { matchesAnySearchRule } from "../../../shared/pipes/filterByProperty";

@Component({
    selector: '[app-keys]',
    templateUrl: './keys.component.html',
    styleUrls: ['./keys.component.css'],
    standalone: false
})
export class KeysComponent extends GroupBaseAdminPanelComponent implements OnDestroy {
  private exportService: ExcelService;
  private containersService: ContainersService;
  private subscription: Subscription;

  public canAddDriverKeyGroup: boolean = false;
  public containerFilterId: number = 0;
  public containers: ContainerViewModel[] = [];
  private containerAclById = new Map<number, number>();
  private containerNameById = new Map<number, string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private keysService: KeysService,
    protected injector: Injector,
    private driversService: DriversService) {
    super(injector);
    this.exportService = injector.get(ExcelService);
    this.containersService = injector.get(ContainersService);

    this.groupName = "KeysGroup";
    this.entityName = "Key";

    this.subscription = this.exportService.onImported.subscribe(data => {
      this.keysService.ExportKeys(data).subscribe(() => {
        this.Refresh();
        this.toastr.success(this.translate.instant('Import successful'));
      });
    });
  }

  override ngOnInit() {
    super.ngOnInit();

    this.usersService.onInit.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.buildContainerAclMap();
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getObjects(): KeyViewModel[] {
    return this.objects as KeyViewModel[];
  }

  protected Refresh() {
    this.loadContainers();

    this.keysService.GetKeys().pipe(
      switchMap((keys: KeyViewModel[]) =>
        this.driversService.GetAllDrivers().pipe(
          map(drivers => {
            const driverMap = new Map<number, string>();
            drivers.forEach(driver => driverMap.set(driver.Id, driver.Name));

            return keys.map(key => {
              key.DriverName = driverMap.get(key.DriverId) || "";
              return key;
            });
          })
        )
      )
    ).subscribe(updatedKeys => {
      this.objects = updatedKeys;
    });

    this.keysService.GetGroups().subscribe(groups => {
      this.groupFilter.groups = groups;
    });
  }

  public get filteredKeys(): KeyViewModel[] {
    let items = this.objects as KeyViewModel[];

    const term = this.filter.term ?? "";
    if (term) {
      items = items.filter(key =>
        matchesAnySearchRule(term, key.Name, key.UID, key.Comment));
    }

    if (this.filter.group?.Id > 0) {
      const groupIds = new Set(this.filter.group.objects ?? []);
      items = items.filter(key => groupIds.has(key.Id));
    }

    if (this.containerFilterId > 0) {
      items = items.filter(key => key.ContainerId === this.containerFilterId);
    }

    return items;
  }

  public getContainerName(containerId?: number): string {
    if (!containerId)
      return "";

    return this.containerNameById.get(containerId) ?? "";
  }

  public openCreateModal() {
    if (!this.canAddDriverKey()) return;

    this.prepareModal(CreateKeyComponent);
    this.componentRef.instance.open(new KeyViewModel());
  }

  public openEditModal(key: KeyViewModel) {
    this.prepareModal(EditKeyComponent);
    this.componentRef.instance.open(key);
  }

  public onEditKey(key: KeyViewModel, event?: Event) {
    event?.stopPropagation();
    if (!this.canEditDriverKey(key)) return;
    this.openEditModal(key);
  }

  public onDeleteKey(key: KeyViewModel, event?: Event) {
    event?.stopPropagation();
    if (!this.canDeleteDriverKey(key)) return;
    this.deleteObject(key.Id);
  }

  protected doDeleteObject(id: number): Observable<any> {
    return this.keysService.DeleteKey(id);
  }

  openSyncKeysDialog(obj: KeyViewModel) {
    this.prepareModal(KeyObjectsSyncDialogComponent);
    this.componentRef.instance.keyId = obj.Id;
    this.componentRef.instance.open();
  }

  Incomingfile(event) {
    const file = event.target.files[0];
    if (file)
      this.exportService.Upload(file);
  }

  OpenExport() {
    this.exportService.exportJSONAsExcelFile(this.objects.filter(c => c.IsChecked));
  }

  protected doCreateGroup(input: IManagableObject): Observable<any> {
    return this.keysService.CreateGroup(input);
  }

  protected doDeleteGroup(input: IManagableObject): Observable<any> {
    return this.keysService.DeleteGroup(input);
  }

  public override addGroup() {
    const creatableContainers = this.getCreatableContainers(this.containers ?? []);
    if (!creatableContainers.length) return;

    this.groupMgmtDialog = this.dialogService.open(this.groupsContainer, GroupManagementDialogComponent);
    this.groupMgmtDialog.instance.containers = creatableContainers;
    this.groupMgmtDialog.instance.readonlyContainer = false;
    this.initDriverKeyGroupDialog();

    const group = new ObjectGroupViewModel();
    group.ContainerId = creatableContainers[0].Id;
    this.groupMgmtDialog.instance.open(group);
  }

  public override editGroup(group: ObjectGroupViewModel) {
    this.groupMgmtDialog = this.dialogService.open(this.groupsContainer, GroupManagementDialogComponent);
    this.groupMgmtDialog.instance.containers = this.containers ?? [];
    this.groupMgmtDialog.instance.readonlyContainer = true;
    this.initDriverKeyGroupDialog();
    this.groupMgmtDialog.instance.open(group);
  }

  public canAddDriverKey(): boolean {
    const user = this.usersService.getCurrentUser();
    if (!user) return false;

    if (user.CanDo(this.AccessRights.EDITUSERS) || user.CanDo(this.AccessRights.EDIT_ALL_USERS))
      return true;

    for (const acl of this.containerAclById.values()) {
      if ((acl & ContainerAcl.CreateDriverKey) === ContainerAcl.CreateDriverKey)
        return true;
    }

    return false;
  }

  public canEditDriverKey(key: KeyViewModel): boolean {
    return this.hasDriverKeyPermission(key, ContainerAcl.EditDriverKey);
  }

  public canDeleteDriverKey(key: KeyViewModel): boolean {
    return this.hasDriverKeyPermission(key, ContainerAcl.DeleteDriverKey);
  }

  private hasDriverKeyPermission(key: KeyViewModel, permission: ContainerAcl): boolean {
    const user = this.usersService.getCurrentUser();
    if (!user) return false;

    if (user.CanDo(this.AccessRights.EDITUSERS) || user.CanDo(this.AccessRights.EDIT_ALL_USERS))
      return true;

    if (!key?.ContainerId)
      return false;

    const acl = this.containerAclById.get(key.ContainerId) ?? 0;
    return (acl & permission) === permission;
  }

  private loadContainers() {
    const currentUser = this.usersService.getCurrentUser();
    if (!currentUser) {
      this.containers = [];
      this.containerNameById = new Map<number, string>();
      this.containerFilterId = 0;
      this.buildContainerAclMap();
      this.canAddDriverKeyGroup = false;
      return;
    }

    this.containersService.GetContainers().subscribe(containers => {
      this.containers = [...(containers ?? [])]
        .sort((a, b) => (a.Name ?? "").localeCompare(b.Name ?? ""));
      this.containerNameById = new Map(this.containers.map(container => [container.Id, container.Name] as [number, string]));
      if (this.containerFilterId > 0 && !this.containers.some(container => container.Id === this.containerFilterId))
        this.containerFilterId = 0;
      this.buildContainerAclMap();
      this.canAddDriverKeyGroup = this.getCreatableContainers(this.containers).length > 0;
    });
  }

  private buildContainerAclMap() {
    this.containerAclById = new Map<number, number>();

    for (const container of this.containers) {
      this.containerAclById.set(container.Id, this.getContainerAcl(container));
    }
  }

  private getCreatableContainers(containers: ContainerViewModel[]): ContainerViewModel[] {
    return containers.filter(container =>
      (this.getContainerAcl(container) & ContainerAcl.CreateDriverKey) === ContainerAcl.CreateDriverKey);
  }

  private getContainerAcl(container: ContainerViewModel): number {
    const currentUser = this.usersService.getCurrentUser();
    if (!currentUser)
      return 0;

    if (currentUser.CanDo(this.AccessRights.EDITUSERS) || currentUser.CanDo(this.AccessRights.EDIT_ALL_USERS))
      return ContainerAcl.CreateDriverKey | ContainerAcl.EditDriverKey | ContainerAcl.DeleteDriverKey;

    const userId = currentUser.Id;
    const aclFromUsers = container.Users?.find(link => link.UserId === userId)?.ACL;
    const fallbackAcl = (currentUser.ContainerLinks ?? []).find(link => link.ContainerId === container.Id)?.ACL;
    return aclFromUsers ?? fallbackAcl ?? 0;
  }

  private initDriverKeyGroupDialog() {
    const allObjects = this.objects.map(x => Object.assign({}, x)) as KeyViewModel[];

    this.groupMgmtDialog.instance.onSubmit.subscribe(() => {
      const input = this.groupMgmtDialog.instance.object as ObjectGroupViewModel;
      input.objects = this.groupMgmtDialog.instance.objects.map(key => key.Id);
      this.doCreateGroup(input).subscribe(() => {
        if (this.groupName)
          this.syncService.NotifyEntityChanged(this.groupName, input.Name);
        this.Refresh();
      });
      this.groupMgmtDialog.instance.close();
    });

    this.groupMgmtDialog.instance.onOpen.subscribe(() => {
      const group = this.groupMgmtDialog.instance.object as ObjectGroupViewModel;
      this.groupMgmtDialog.instance.objects = this.getVisibleGroupKeys(allObjects, group.ContainerId, group.objects)
        .filter(key => (group.objects ?? []).indexOf(key.Id) >= 0)
        .map(key => {
          key.IsChecked = false;
          return key;
        });
    });

    this.groupMgmtDialog.instance.onAddObjects.subscribe(() => {
      this.objectsListDialog = this.dialogService.open(this.objectsListContainer, ObjectsManagementDialogComponent);
      this.objectsListDialog.instance.onOpen.subscribe(() => {
        const group = this.groupMgmtDialog.instance.object as ObjectGroupViewModel;
        const filteredObjects = this.getVisibleGroupKeys(allObjects, group.ContainerId, group.objects)
          .filter(key => this.groupMgmtDialog.instance.objects.find(existing => existing.Id == key.Id) == undefined)
          .map(x => Object.assign({}, x))
          .map(key => {
            key.IsChecked = false;
            return key;
          });

        this.objectsListDialog.instance.groups = this.groupFilter.groups;
        this.objectsListDialog.instance.objects = filteredObjects;
      });

      this.objectsListDialog.instance.onSubmit.subscribe(() => {
        this.objectsListDialog.instance.objects.forEach(key => {
          if (key && key.IsChecked) {
            if (this.groupMgmtDialog.instance.objects.indexOf(key) < 0) {
              key.IsChecked = false;
              this.groupMgmtDialog.instance.objects.push(key);
            }
          }
        });
        this.objectsListDialog.instance.close();
      });

      this.objectsListDialog.instance.open();
    });
  }

  private getVisibleGroupKeys(allObjects: KeyViewModel[], containerId?: number, selectedIds?: number[]): KeyViewModel[] {
    const ids = selectedIds ?? [];
    return allObjects.filter(key => {
      if (ids.includes(key.Id))
        return true;

      if (!containerId)
        return false;

      return key.ContainerId === containerId;
    });
  }
}
